import { $isListItemNode, type ListItemNode } from "@lexical/list";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  defineExtension,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  type LexicalExtension,
} from "lexical";
import { $canIndent } from "./depth";

/**
 * Tab and Shift+Tab indent only while the selection is inside a list. Everywhere else the key is
 * left to the browser so focus navigation keeps working; upstream's TabIndentationExtension traps
 * Tab across the whole editor. Code blocks handle their own Tab through CodeIndentExtension.
 */
export const ListTabExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/list-tab",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/list-tab",
  register: (editor) =>
    mergeRegister(
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;
          const inList = [selection.anchor, selection.focus].every(
            (point) => $findMatchingParent(point.getNode(), $isListItemNode) !== null,
          );
          if (!inList) return false;
          event.preventDefault();
          return editor.dispatchCommand(
            event.shiftKey ? OUTDENT_CONTENT_COMMAND : INDENT_CONTENT_COMMAND,
            undefined,
          );
        },
        COMMAND_PRIORITY_LOW,
      ),
      // Ahead of upstream's list indent, which has no bound: an indent that would nest the list
      // deeper than a stored document may be is refused, or the editor would save a document it
      // then refuses to open.
      editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;
          const items = new Set<ListItemNode>();
          for (const node of selection.getNodes()) {
            const item = $isListItemNode(node) ? node : $findMatchingParent(node, $isListItemNode);
            if ($isListItemNode(item)) items.add(item);
          }
          return items.size > 0 && !$canIndent([...items]);
        },
        COMMAND_PRIORITY_HIGH,
      ),
    ),
});
